<template>
    <div class="container" v-if="!isDataRequired">
        <v-title :lineage="[{'Organizations':'organizations'}]">
            {{isFormUpdating ? 'Update' : 'Add'}} Organization
            <div slot="actions">
                <v-cancel :defaultRoute="{name: 'organizations'}"></v-cancel>
                <r-button @click="save" :loading="isLoading" type="primary">Save Organization</r-button>
            </div>
        </v-title>
        <v-error-messages
            :has-frontend-errors="$v.organization.$error"
            :server-error="serverError"
            class="stack-l">
            <li class="list-item" v-if="!$v.organization.name.required">Organization name is required</li>
            <li class="list-item" v-if="!$v.organization.id.resourceId"><span>The organization ID cannot contain spaces or special characters other than <strong>@ ~ _ -</strong> and should be less than <strong>50</strong> characters long.</span></li>
            <li class="list-item" v-if="!$v.organization.country.required">Organization country is required</li>
            <template v-if="hasErrors">
                <li class="list-item" v-for="error in validationAggregator.aggregatedResult">{{error.name}}</li>
            </template>
        </v-error-messages>
        <v-form-tile>
            <h2 slot="title">Organization Logo</h2>
            <p slot="description" class="text-muted">
                Optionally add a logo for this organization to be used in official documents like invoices generated by Rebilly.
            </p>
            <div slot="content" class="tile">
                <div class="tile-content">
                    <v-upload-logo
                        :preview-url="previewUrl"
                        @no-logo="setNoLogo"
                        @update="updateLogo"/>
                </div>
            </div>
        </v-form-tile>
        <div class="divider"></div>
        <v-form-tile>
            <h2 slot="title">Organization Information</h2>
            <p slot="description" class="text-muted">Rebilly gives you the flexibility to add as many organizations as you’d
                like, all neatly organized in one place. Need help? <a class="cursor-pointer" @click="contactUs()">Contact us</a>
            </p>
            <div slot="content" class="tile">
                <div class="tile-content">
                    <div class="grid-content">
                        <div class="grid-item grid-item-half grid-item-break-half">
                            <r-input
                                label="Organization Name"
                                auto-focus
                                v-model="organization.name"
                                :validate="$v.organization.name"
                                class="slug-source"
                            />
                        </div>
                        <div class="grid-item grid-item-half grid-item-break-half">
                            <r-input
                                :disabled="!!id"
                                label="Organization ID"
                                v-model="organization.id"
                                :validate="$v.organization.id"
                                help-text="ID used to uniquely identify this website. If not provided, it will be generated automatically."
                                class="slug-target"
                            />
                        </div>
                        <div class="grid-item grid-item-full">
                            <r-input
                                label="Address Line 1"
                                v-model="organization.address"
                                help-text="Street address, P.O. box, company name, c/o"
                            />
                        </div>
                        <div class="grid-item grid-item-full">
                            <r-input
                                label="Address Line 2"
                                v-model="organization.address2"
                                help-text="Apartment, suite, unit, building, floor, etc"
                            />
                        </div>
                        <div class="grid-item grid-item-half grid-item-break-half">
                            <r-input
                                label="City"
                                v-model="organization.city"
                            />
                        </div>
                        <div class="grid-item grid-item-half grid-item-break-half">
                            <v-select
                                label="Country"
                                :options="countryList"
                                placeholder="Select Country"
                                v-model="organization.country"
                                :validate="$v.organization.country"
                                @input="clearRegion"
                            />
                        </div>
                        <div class="grid-item grid-item-half grid-item-break-half">
                            <v-select
                                label="Region"
                                v-if="hasRegion"
                                :options="regionNames"
                                option-label="regionName"
                                option-key="regionCode"
                                placeholder="Select Region"
                                v-model="organization.region"
                            />
                            <r-input
                                v-else
                                label="Region (state or province)"
                                v-model="organization.region"
                            />
                        </div>
                        <div class="grid-item grid-item-half grid-item-break-half">
                            <r-input
                                label="Postal Code"
                                v-model="organization.postalCode"
                            />
                        </div>
                        <div class="grid-item grid-item-full">
                            <contact-phones-emails
                                ref="phoneNumbersAndEmails"
                                :aggregator="validationAggregator"
                                v-model="organization"/>
                        </div>
                        <div class="grid-item grid-item-half grid-item-break-half">
                            <r-input
                                label="Tax Descriptor"
                                v-model="organization.taxDescriptor"
                            />
                        </div>
                        <div class="grid-item grid-item-half grid-item-break-half">
                            <v-select
                                label="Invoice Time Zone"
                                option-label="label"
                                option-key="name"
                                v-model="organization.invoiceTimeZone"
                                :options="timezoneOptions"/>
                        </div>
                    </div>
                </div>
                <div class="tile-content">
                    <div class="grid-content">
                        <!-- TODO: clear this - seems weird to me, but I didn't dive deep into it -->
                        <div class="grid-item grid-item-half grid-item-break-half" v-if="!isPrimary">
                            <r-checkbox
                                v-model="organization.isPrimary"
                                :value="true"
                                label="Set this organization as primary"/>
                        </div>
                        <div class="grid-item grid-item-half grid-item-break-half" v-else>
                            <r-checkbox
                                :value="false"
                                :disabled="true"
                                label="This organization has been set as primary"/>
                        </div>
                    </div>
                </div>
            </div>
        </v-form-tile>
        <div class="divider"></div>
        <div class="grid-fitted stack-l">
            <div class="grid-item text-right equal-widths-elements-mobile">
                <v-cancel :defaultRoute="{name: 'organizations'}"></v-cancel>
                <r-button @click="save" :loading="isLoading" type="primary">Save Organization</r-button>
            </div>
        </div>
    </div>
</template>

<script>
    import vTitle from '@/components/v-title';
    import {
        vCancel,
        vErrorMessages,
        vFormTile,
        vIcon,
        vIconSprites,
        vSelect,
        vUploadLogo,
    } from '@/components/form';
    import {COUNTRIES_LIST} from '@/data/countries';
    import REGIONS from '@/data/regions';
    import {email, required} from 'vuelidate/lib/validators';
    import resourceId from '@/components/vuelidate/validators/resource-id';
    import FormLoaderMixin from '@/mixins/form-loader-mixin';
    import sluggifyMixin from '@/mixins/sluggify-mixin';
    import OrganizationModel from '@/common/models/organization-model';
    import ValidationAggregator from '@/common/validation/validation-aggregator';
    import ContactPhonesEmails from '@/components/contact-manager/contact-phones-emails';
    import timezoneOptions from '@/data/timezones/processed.json';

    export default {
        name: 'organization-form',
        mixins: [
            FormLoaderMixin,
            sluggifyMixin({
                target: 'organization.id',
                source: 'organization.name',
                entityCallback(id) {
                    return this.$api(({companies}) => companies.get({id}));
                },
            }),
        ],
        components: {
            vTitle,
            vFormTile,
            vIconSprites,
            vCancel,
            vIcon,
            vErrorMessages,
            vSelect,
            vUploadLogo,
            ContactPhonesEmails,
        },
        props: {
            id: {
                type: [String, Number],
                default: null,
            },
        },
        data() {
            return {
                organization: new OrganizationModel(),
                countryList: COUNTRIES_LIST,
                regionList: {...REGIONS},
                logo: null,
                fileId: null,
                previewUrl: null,
                validationAggregator: new ValidationAggregator(),
                isPrimary: false,
                timezoneOptions: [...timezoneOptions],
            };
        },
        validations: {
            organization: {
                id: {resourceId},
                name: {required},
                country: {required},
                phoneNumbers: {
                    $each: {
                        value: {
                            required,
                        },
                    },
                },
                emails: {
                    $each: {
                        value: {
                            required,
                            email,
                        },
                    },
                },
            },
        },
        computed: {
            regionNames() {
                return this.regionList[this.organization.country];
            },
            hasRegion() {
                return ['CA', 'US'].includes(this.organization.country);
            },
            repeatMessage() {
                return this.continue !== null;
            },
            hasErrors() {
                return this.validationAggregator.validateAggregated();
            },
        },
        methods: {
            clearRegion() {
                this.organization.region = '';
            },
            updateLogo(file) {
                this.logo = file;
            },
            setNoLogo() {
                this.logo = null;
                this.previewUrl = null;
            },
            contactUs() {
                this.triggerIntercomEvent({
                    name: `Contact Us`,
                    meta: {
                        url: this.$route.fullPath,
                    },
                });
            },
            async save() {
                this.$v.$touch();
                if (!this.$v.organization.$error && !this.validationAggregator.validateAggregated()) {
                    this.isLoading = true;
                    try {
                        const params = {
                            data: this.organization.getPayload(),
                        };
                        if (this.organization.id) {
                            params.id = this.organization.id;
                        }
                        if (this.makePrimary) {
                            params.data.isPrimary = this.makePrimary;
                        }
                        const method = this.id ? 'update' : 'create';
                        const organization = this.id
                            ? await this.$api(({companies}) => companies.update(params))
                            : await this.$api(({companies}) => companies.create(params));
                        window.scrollTo(0, 0);
                        await this.processLogo(organization);
                        this.$toast.positive(`Organization successfully ${method}d`);
                        if (!this.isFormUpdating) {
                            this.$router.push({name: 'add-website', params: {organizationId: organization.fields.id}});
                        }
                    } catch (error) {
                        this.serverError = error;
                    } finally {
                        this.fetchData();
                        this.isLoading = false;
                    }
                }
            },
            async processLogo({fields}) {
                const {id} = fields;
                try {
                    if (this.logo !== null && this.fileId === null) {
                        await this.uploadLogo(id);
                    } else if (this.logo !== null && this.fileId !== null) {
                        await Promise.all([
                            this.deleteExistingLogo(),
                            this.uploadLogo(id),
                        ]);
                    } else if (this.logo === null && this.fileId !== null) {
                        await this.deleteExistingLogo();
                    }
                } catch (error) {
                    this.$toast.fromError(error, 'We were unable to update the organization logo. Please try again later or contact support.');
                }
            },
            async uploadLogo(relatedId) {
                const form = new FormData();
                form.append('file', this.logo);

                const file = await this.$api(({files}) => files.upload({fileObject: form}));
                this.fileId = file.fields.id;
                const data = {
                    relatedType: 'organization',
                    relatedId,
                    fileId: this.fileId,
                };
                await this.$api(({files}) => files.attach({data}));
            },
            async deleteExistingLogo() {
                await this.$api(({files}) => files.detachAndDelete({id: this.fileId}));
            },
            async getAttachment() {
                const {items, total} = await this.$api(({files}) => files.getAllAttachments({
                    filter: `relatedId:${this.organization.id};relatedType:organization`,
                    sort: '-createdTime',
                }));
                if (total > 0) {
                    const [attachment] = items;
                    const {fields} = await this.$api(({files}) => files.get({id: attachment.fields.fileId}));
                    /* eslint-disable no-underscore-dangle */
                    this.previewUrl = fields._links.find(link => link.rel === 'signedLink').href;
                    this.fileId = fields.id;
                } else {
                    this.fileId = null;
                }
            },
            async fetchData() {
                if (!this.isFormUpdating) {
                    return;
                }
                try {
                    const {fields} = await this.$api(({companies}) => companies.get({id: this.id}));
                    this.organization = new OrganizationModel(fields);
                    this.isPrimary = this.organization.isPrimary;
                    await this.getAttachment();
                } catch (error) {
                    this.$redirectOnError(error, {name: 'organizations'});
                }
            },
        },
    };
</script>
